import api from '@lib/axios';

export interface OnboardingState {
  current_step: number;
  steps: Array<{
    key: string;
    title: string;
    status: 'completed' | 'active' | 'pending';
  }>;
  wa_number: string | null;
  wallet_balance: string;
  template_created: boolean;
}

export async function getOnboardingState(): Promise<OnboardingState> {
  const { data } = await api.get<{ data: OnboardingState }>('/onboarding/state');
  return data.data;
}

export async function skipStep(stepKey: string): Promise<void> {
  await api.post('/onboarding/skip', { step: stepKey });
}

export async function completeOnboarding(): Promise<void> {
  await api.post('/onboarding/complete');
}

// ---------------------------------------------------------------------------
// Embedded signup (P4·S6)
// ---------------------------------------------------------------------------

export interface EmbeddedSignupFbConfig {
  app_id: string;
  config_id: string;
  redirect_uri: string;
  scope: string;
}

export interface StartEmbeddedSignupResult {
  project_id: string;
  fb_config: EmbeddedSignupFbConfig;
}

export interface ExchangeFbCodeResult {
  status: 'submitted';
  poll_url: string;
}

export type ProvisioningUiStatus =
  | 'provisioning'
  | 'awaiting_activation'
  | 'activated'
  | 'failed';

export interface ProvisioningStateResult {
  project_id: string;
  name: string;
  status: ProvisioningUiStatus;
  wa_number: string | null;
  error_message: string | null;
  activation_progress: number;
}

/**
 * P4·S6 — Begin Meta embedded signup.
 *
 *   1. Backend creates Aisensy business + project.
 *   2. Returns the FB config the frontend uses with `FB.login`.
 *
 * The flow continues with `exchangeFbCode` once the FB popup
 * resolves with an OAuth code.
 */
/** Static FB embedded-signup config — used by the Add Number modal (no side effects). */
export async function getFbConfig(): Promise<EmbeddedSignupFbConfig> {
  const { data } = await api.get<{ data: EmbeddedSignupFbConfig }>(
    '/onboarding/fb-config',
    { skipProjectId: true },
  );
  return data.data;
}

export async function startEmbeddedSignup(
  projectName: string,
): Promise<StartEmbeddedSignupResult> {
  const { data } = await api.post<{ data: StartEmbeddedSignupResult }>(
    '/onboarding/embedded-signup/start',
    { project_name: projectName },
  );
  return data.data;
}

export async function exchangeFbCode(
  projectId: string,
  code: string,
  sessionInfo?: { waba_id?: string; phone_number_id?: string },
): Promise<ExchangeFbCodeResult> {
  const { data } = await api.post<{ data: ExchangeFbCodeResult }>(
    '/onboarding/embedded-signup/exchange-fb-code',
    {
      project_id: projectId,
      code,
      waba_id: sessionInfo?.waba_id,
      phone_number_id: sessionInfo?.phone_number_id,
    },
  );
  return data.data;
}

export async function getProvisioningState(
  projectId: string,
): Promise<ProvisioningStateResult> {
  const { data } = await api.get<{ data: ProvisioningStateResult }>(
    `/onboarding/embedded-signup/state/${encodeURIComponent(projectId)}`,
  );
  return data.data;
}
